import { useRef, useState, useEffect, useCallback } from "react";

/**
 * Wiederverwendbarer Hook für die Web Speech API (Spracherkennung / Diktat).
 * Kostenlos, läuft direkt im Browser (am besten Chrome/Android). Deutsch (de-DE).
 *
 * @param {Object} opts
 * @param {string} [opts.lang="de-DE"] Erkennungssprache
 * @param {(text:string)=>void} [opts.onResult] Callback mit finalem Transkript-Segment
 * @param {(e:any)=>void} [opts.onError] Fehler-Callback
 * @param {boolean} [opts.continuous=true] Fortlaufendes Diktat
 */
export function useSpeechRecognition({ lang = "de-DE", onResult, onError, continuous = true } = {}) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);

  const SR =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = !!SR;

  const stop = useCallback(() => {
    try {
      recRef.current?.stop();
    } catch {
      // ignore
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!SR) return;
    try {
      recRef.current?.abort();
    } catch {
      // ignore
    }
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) finalText += result[0].transcript;
      }
      const cleaned = finalText.trim();
      if (cleaned && onResult) onResult(cleaned);
    };
    rec.onerror = (e) => {
      if (onError) onError(e);
      setListening(false);
    };
    rec.onend = () => setListening(false);

    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      // start() wirft, wenn bereits aktiv – ignorieren
    }
  }, [SR, lang, continuous, onResult, onError]);

  useEffect(
    () => () => {
      try {
        recRef.current?.abort();
      } catch {
        // ignore
      }
    },
    []
  );

  return { listening, supported, start, stop };
}
